import { isValidElement, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { lerGaleria } from '../../paineis';
import type { GaleriaDeGraficos, Periodo } from '../../paineis';
import { inicioDaSemana } from '../semana';

/**
 * PERÍODO DE CADA GRÁFICO — cada cartão escolhe o seu: o da página, hoje, esta semana (sábado a
 * sexta, como o cheque), este mês, este trimestre, este ano (do 1º dia até hoje) ou um período
 * livre. A escolha fica lembrada neste aparelho. Cartões com o mesmo período dividem uma busca só
 * (cache de 1 minuto): trocar o período de um gráfico não refaz a tela inteira.
 */

export type TipoDePeriodo = 'pagina' | 'hoje' | 'semana' | 'mes' | 'trimestre' | 'ano' | 'livre';
export const TIPOS: readonly [TipoDePeriodo, string][] = [
  ['pagina', 'Página'],
  ['hoje', 'Hoje'],
  ['semana', 'Semana'],
  ['mes', 'Mês'],
  ['trimestre', 'Trimestre'],
  ['ano', 'Ano'],
  ['livre', 'Período'],
];

const hojeEmMuriae = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

export function periodoDoTipo(
  tipo: Exclude<TipoDePeriodo, 'pagina' | 'livre'>,
  hoje = hojeEmMuriae(),
): Periodo {
  const [a = '2026', m = '01'] = hoje.split('-');
  if (tipo === 'hoje') return { de: hoje, ate: hoje };
  if (tipo === 'semana') {
    return { de: inicioDaSemana(hoje), ate: hoje };
  }
  if (tipo === 'mes') return { de: `${a}-${m}-01`, ate: hoje };
  if (tipo === 'trimestre') {
    const inicio = String(Math.floor((Number(m) - 1) / 3) * 3 + 1).padStart(2, '0');
    return { de: `${a}-${inicio}-01`, ate: hoje };
  }
  return { de: `${a}-01-01`, ate: hoje };
}

/* cache compartilhado entre os cartões (mesmo período + pessoa = uma busca só) */
const CACHE = new Map<string, { em: number; p: Promise<GaleriaDeGraficos | null> }>();
const chave = (p: Periodo, pessoa: string | null) => `${p.de}|${p.ate}|${pessoa ?? ''}`;
export function semearGaleria(g: GaleriaDeGraficos, pessoa: string | null = null) {
  CACHE.set(chave(g.periodo, pessoa), { em: Date.now(), p: Promise.resolve(g) });
}
function buscar(p: Periodo, pessoa: string | null): Promise<GaleriaDeGraficos | null> {
  const k = chave(p, pessoa);
  const c = CACHE.get(k);
  if (c && Date.now() - c.em < 60_000) return c.p;
  const nova = lerGaleria(p, pessoa).then((r) => (r.ok ? r.dados : null));
  CACHE.set(k, { em: Date.now(), p: nova });
  return nova;
}

function lembrado(id: string): { tipo: TipoDePeriodo; de?: string; ate?: string } {
  try {
    const v = JSON.parse(window.localStorage.getItem(`inovapay:periodo:${id}`) ?? 'null') as {
      tipo?: string;
      de?: string;
      ate?: string;
    } | null;
    if (v && TIPOS.some(([t]) => t === v.tipo))
      return v as { tipo: TipoDePeriodo; de?: string; ate?: string };
  } catch {
    // sem armazenamento
  }
  return { tipo: 'pagina' };
}

export function ComPeriodo({
  id,
  padrao,
  pessoaId = null,
  children,
}: {
  id: string;
  /** O período da página (usado em "Página"). */
  padrao: Periodo;
  pessoaId?: string | null;
  children: (g: GaleriaDeGraficos | null, seletor: ReactNode, carregando: boolean) => ReactNode;
}) {
  const inicial = lembrado(id);
  const [tipo, setTipo] = useState<TipoDePeriodo>(inicial.tipo);
  const [livre, setLivre] = useState<Periodo>({
    de: inicial.de ?? padrao.de,
    ate: inicial.ate ?? padrao.ate,
  });
  const periodo: Periodo =
    tipo === 'pagina' ? padrao : tipo === 'livre' ? livre : periodoDoTipo(tipo);
  const [g, setG] = useState<GaleriaDeGraficos | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    void buscar(periodo, pessoaId).then((d) => {
      if (!vivo) return;
      setG(d);
      setCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [periodo.de, periodo.ate, pessoaId]);

  const guardar = (t: TipoDePeriodo, p?: Periodo) => {
    try {
      window.localStorage.setItem(
        `inovapay:periodo:${id}`,
        JSON.stringify({ tipo: t, ...(p ?? {}) }),
      );
    } catch {
      // sem armazenamento
    }
  };
  const valido =
    livre.de <= livre.ate && (Date.parse(livre.ate) - Date.parse(livre.de)) / 86_400_000 <= 365;

  const seletor = (
    <div className="gx-periodo">
      <label>
        <span>Período</span>
        <select
          value={tipo}
          aria-label="Período deste gráfico"
          onChange={(e) => {
            const t = e.target.value as TipoDePeriodo;
            setTipo(t);
            guardar(t, t === 'livre' ? livre : undefined);
          }}
        >
          {TIPOS.map(([t, nome]) => (
            <option key={t} value={t}>
              {nome}
            </option>
          ))}
        </select>
      </label>
      {tipo === 'livre' && (
        <span className="gx-livre">
          <input
            type="date"
            value={livre.de}
            max={livre.ate}
            aria-label="De"
            onChange={(e) => {
              const p = { ...livre, de: e.target.value };
              setLivre(p);
              guardar('livre', p);
            }}
          />
          <input
            type="date"
            value={livre.ate}
            min={livre.de}
            aria-label="Até"
            onChange={(e) => {
              const p = { ...livre, ate: e.target.value };
              setLivre(p);
              guardar('livre', p);
            }}
          />
          {!valido && <em>até 1 ano</em>}
        </span>
      )}
    </div>
  );
  return <>{children(valido || tipo !== 'livre' ? g : null, seletor, carregando)}</>;
}

/** Acha, na árvore, o cartão com este `id` (prop do CartaoDeGrafico). */
export function acharCartao(
  no: ReactNode,
  id: string,
): ReactElement<{ id?: string; periodo?: ReactNode }> | null {
  if (Array.isArray(no)) {
    for (const x of no as readonly ReactNode[]) {
      const r = acharCartao(x, id);
      if (r) return r;
    }
    return null;
  }
  if (!isValidElement<{ id?: string; children?: ReactNode }>(no)) return null;
  if (no.props.id === id) return no as ReactElement<{ id?: string; periodo?: ReactNode }>;
  return acharCartao(no.props.children, id);
}
